import type {
  CreateManagementPayload,
  CreateManagementResponse,
  DeleteManagementResponse,
  GetManagementResponse,
  UpdateManagementPayload,
  UpdateManagementResponse,
} from '@/domains/academicRisk';
import { ApiEndPoint } from '@/domains/api/api.entity';
import { apiRequest } from './apiRequest';

/**
 * Servicio API para gestión/ejecución de acciones académicas
 * Responsable de las comunicaciones HTTP relacionadas con el seguimiento y ejecución
 * de acciones previamente registradas
 */
class AcademicRiskManagementApiService {
  /**
   * Obtiene todas las gestiones/ejecuciones asociadas a una incorporación de acción específica
   * @param cod_incorporacion_accion - Código único de la incorporación de acción
   */
  async getManagement(
    cod_incorporacion_accion: number,
  ): Promise<GetManagementResponse> {
    const managementResponse = await apiRequest.get<GetManagementResponse>({
      route: ApiEndPoint.MANAGEMENT_ACTION,
      param: `/incorporacion/${cod_incorporacion_accion}`,
    });

    return managementResponse;
  }

  /**
   * Registra una nueva gestión/ejecución sobre una acción académica existente
   * @param payload - Datos de la gestión incluyendo fecha de ejecución, funcionario responsable y detalle
   */
  async createManagement(
    payload: CreateManagementPayload,
  ): Promise<CreateManagementResponse> {
    const createManagementResponse = await apiRequest.post<
      CreateManagementResponse,
      CreateManagementPayload
    >({
      route: ApiEndPoint.MANAGEMENT_ACTION,
      payload,
    });

    return createManagementResponse;
  }

  /**
   * Actualiza una gestión/ejecución de acción existente
   * @param cod_ejecucion - Código único de la ejecución a actualizar
   * @param payload - Datos actualizados de la gestión (acción sobre riesgo y detalle)
   */
  async updateManagement(
    cod_ejecucion: number,
    payload: UpdateManagementPayload,
  ): Promise<UpdateManagementResponse> {
    const updateManagementResponse = await apiRequest.put<
      UpdateManagementResponse,
      UpdateManagementPayload
    >({
      route: ApiEndPoint.MANAGEMENT_ACTION,
      param: `/${cod_ejecucion}`,
      payload,
    });

    return updateManagementResponse;
  }

  /**
   * Elimina una gestión/ejecución de acción del sistema
   * @param cod_ejecucion - Código único de la ejecución a eliminar
   */
  async deleteManagement(
    cod_ejecucion: number,
  ): Promise<DeleteManagementResponse> {
    const deleteManagementResponse =
      await apiRequest.delete<DeleteManagementResponse>({
        route: ApiEndPoint.MANAGEMENT_ACTION,
        param: `/${cod_ejecucion}`,
      });

    return deleteManagementResponse;
  }
}

export const academicRiskManagementApiService =
  new AcademicRiskManagementApiService();
